import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import {initialState} from '@/store/states'
import {
  AdvisorInterface,
  AxiosResponseAdvisorWithCoordinatesInterface,
  AxiosResponseBoosterInterface,
  FilledOrbInterface,
  HexaTradingOptionsInterface,
  MinerInventoryInterface,
  MiscInventoryInterface,
  ObjectOfMapInterface,
  OrbRInterface,
  ResourceInterface,
  TemplatesOrbsRInterface,
  UpgradesOfAdvisorInterface,
  UserInterface,
} from '@/types/types'

export const slice = createSlice({
  name: 'index',
  initialState,
  reducers: {
    CLEAR_LOCAL_STORAGE: (state) => {},
    CLEAR_USER_DATA: (state) => {
      state.user = initialState.user
      state.hexId = initialState.hexId
      state.addedObject = initialState.addedObject
      state.orbs = initialState.orbs
      state.billboards = initialState.billboards
      state.templatesOrbsR = initialState.templatesOrbsR
      state.defaultOctaCoinOrb = initialState.defaultOctaCoinOrb
      state.defaultOctaCoinOrbR = initialState.defaultOctaCoinOrbR
      state.advisors = initialState.advisors
      state.advisor = initialState.advisor
      state.advisorOnMap = initialState.advisorOnMap
      state.advisorDashboard = initialState.advisorDashboard
      state.boosters = initialState.boosters
      state.inventories = initialState.inventories
      state.miners = initialState.miners
      state.misc = initialState.misc
      state.timers = initialState.timers
      state.storeTab = initialState.storeTab
      state.lastCoordinatesMain = initialState.lastCoordinatesMain
      state.lastCoordinatesAnalytics = initialState.lastCoordinatesAnalytics
    },
    SET_USER: (state, {payload}: PayloadAction<null | UserInterface>) => {
      state.user = payload
    },
    SET_LOAD: (state, {payload}: PayloadAction<boolean>) => {
      state.isLoad = payload
    },
    SET_HEX_ID: (state, {payload}: PayloadAction<string | null>) => {
      state.hexId = payload
    },
    SET_ADDED_OBJECT: (
      state,
      {
        payload,
      }: PayloadAction<{
        img: string
        id: number
        refId?: number
        kind?: 'orb' | 'miner'
        data: any
      } | null>
    ) => {
      state.addedObject = payload
    },
    SET_MODAL_OF_TYPE_MODE: (state, {payload}: PayloadAction<string>) => {
      state.typeModalOfMap = payload
    },
    SET_ORBS_TYPE: (state, {payload}: PayloadAction<1 | 2 | 3 | 4 | 5>) => {
      state.orbsType = payload
    },
    SET_CLUE_FOR_MODAL: (
      state,
      {payload}: PayloadAction<{index: number; time?: number; data: any}>
    ) => {
      state.clueForModal = payload
    },
    SET_ORBS: (
      state,
      {
        payload,
      }: PayloadAction<{
        1: ObjectOfMapInterface[]
        2: ObjectOfMapInterface[]
        3: ObjectOfMapInterface[]
      }>
    ) => {
      state.orbs = payload
    },
    SET_BILLBOARDS: (
      state,
      {
        payload,
      }: PayloadAction<{
        inventories: OrbRInterface[]
        filled: FilledOrbInterface[]
      }>
    ) => {
      state.billboards = payload
    },
    SET_HEXATRADING_OPTIONS: (
      state,
      {payload}: PayloadAction<HexaTradingOptionsInterface>
    ) => {
      state.hexaTradingOptions = payload
    },
    SET_IS_NEED_AUTH: (
      state,
      {payload}: PayloadAction<null | 'AUTH' | 'REGISTER'>
    ) => {
      state.isNeedLogin = payload
    },
    SET_TEMPLATES_R_ORBS: (
      state,
      {payload}: PayloadAction<TemplatesOrbsRInterface[]>
    ) => {
      state.templatesOrbsR = payload
    },
    SET_FAIL_LOAD: (state, {payload}: PayloadAction<boolean>) => {
      state.isFailLoad = payload
    },
    SET_DEFAULT_OCTA_COIN_ORB: (state, {payload}: PayloadAction<number>) => {
      state.defaultOctaCoinOrb = payload
    },
    SET_DEFAULT_OCTA_COIN_R_ORB: (state, {payload}: PayloadAction<number>) => {
      state.defaultOctaCoinOrbR = payload
    },
    SET_LANGUAGE: (state, {payload}: PayloadAction<string>) => {
      state.language = payload
    },
    SET_LAST_COORDINATES_MAIN: (
      state,
      {payload}: PayloadAction<[number, number, number]>
    ) => {
      state.lastCoordinatesMain = payload
    },
    SET_LAST_COORDINATES_ANALYTICS: (
      state,
      {payload}: PayloadAction<[number, number, number]>
    ) => {
      state.lastCoordinatesAnalytics = payload
    },
    SET_ADVISORS: (state, {payload}: PayloadAction<AdvisorInterface[]>) => {
      state.advisors = payload
    },
    SET_ADVISOR: (state, {payload}: PayloadAction<AdvisorInterface>) => {
      state.advisor = payload
    },
    SET_ADVISOR_ID_ON_MAP: (
      state,
      {payload}: PayloadAction<AdvisorInterface | null>
    ) => {
      state.advisorOnMap = payload
    },
    SET_NAME_ADVISOR: (
      state,
      {
        payload,
      }: PayloadAction<{
        name: string
        id: number
        description: string | null
      }>
    ) => {
      state.advisor.name = payload.name
      const advisor = state.advisors.find(
        (advisor) => advisor.id === payload.id
      )
      if (advisor) {
        advisor.name = payload.name
        advisor.u_description = payload.description
      }
    },
    SET_NEW_BOOSTER_IN_ADVISOR: (
      state,
      {
        payload,
      }: PayloadAction<{boosters: UpgradesOfAdvisorInterface; id: number}>
    ) => {
      state.advisor.upgrades = payload.boosters
      const advisor = state.advisors.find(
        (advisor) => advisor.id === payload.id
      )
      if (advisor) {
        advisor.upgrades = payload.boosters
      }
    },
    SET_ADVISOR_DASHBOARD: (
      state,
      {
        payload,
      }: PayloadAction<AxiosResponseAdvisorWithCoordinatesInterface | null>
    ) => {
      state.advisorDashboard = payload
    },
    SET_STORE_TAB: (state, {payload}: PayloadAction<0 | 1 | 2 | number>) => {
      state.storeTab = payload
    },
    SET_BOOSTERS: (
      state,
      {payload}: PayloadAction<AxiosResponseBoosterInterface[]>
    ) => {
      state.boosters.list = payload
      state.boosters.isLoad = true
    },
    SET_INVENTORY: (state, {payload}: PayloadAction<ResourceInterface[]>) => {
      state.inventories.list = payload
      state.inventories.isLoad = true
    },
    SET_MINERS: (
      state,
      {payload}: PayloadAction<MinerInventoryInterface[]>
    ) => {
      state.miners.list = payload
      state.miners.isLoad = true
    },
    SET_MISC: (state, {payload}: PayloadAction<MiscInventoryInterface[]>) => {
      state.misc.list = payload
      state.misc.isLoad = true
    },
    ADD_TIMER_HEX_BLOCK: (state, action) => {
      state.timers[action.payload] = Date.now() + 120000
    },
    REMOVE_TIMER_HEX_BLOCK: (state, action) => {
      const id = action.payload
      delete state.timers[id]
    },
  },
})

export const {
  CLEAR_USER_DATA,
  SET_USER,
  SET_LOAD,
  SET_IS_NEED_AUTH,
  SET_HEX_ID,
  SET_ADDED_OBJECT,
  SET_CLUE_FOR_MODAL,
  SET_MODAL_OF_TYPE_MODE,
  SET_ORBS_TYPE,
  SET_ORBS,
  SET_BILLBOARDS,
  SET_TEMPLATES_R_ORBS,
  SET_FAIL_LOAD,
  SET_HEXATRADING_OPTIONS,
  SET_DEFAULT_OCTA_COIN_ORB,
  SET_DEFAULT_OCTA_COIN_R_ORB,
  SET_LAST_COORDINATES_ANALYTICS,
  SET_LAST_COORDINATES_MAIN,
  SET_LANGUAGE,
  SET_ADVISORS,
  SET_ADVISOR,
  SET_NAME_ADVISOR,
  SET_NEW_BOOSTER_IN_ADVISOR,
  SET_ADVISOR_ID_ON_MAP,
  SET_ADVISOR_DASHBOARD,
  SET_STORE_TAB,
  SET_INVENTORY,
  SET_MINERS,
  SET_MISC,
  SET_BOOSTERS,
  ADD_TIMER_HEX_BLOCK,
  REMOVE_TIMER_HEX_BLOCK,
} = slice.actions

export default slice.reducer
